import React, { forwardRef, memo, RefObject } from "react";

import { Chip } from "@mui/material";
import MenuItem, { MenuItemProps } from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/system/Box";

const StyledMenuItem = styled(MenuItem)({
  display: "flex",
  justifyContent: "space-between",
  paddingLeft: "4px",
  paddingRight: "4px",
});

const StyledTypography = styled(Typography)({
  paddingLeft: "8px",
  paddingRight: "8px",
  textAlign: "left",
});

const FlexBox = styled(Box)({
  display: "flex",
});

/**
 * Props for Icon Menu Item.
 * @typedef
 * @name IconMenuItemProps
 * @kind variable
 */
type IconMenuItemProps = {
  /**
   * (optional)
   * Props for Menu Item.
   * @property
   * @name MenuItemProps
   * @type {MenuItemProps | undefined}
   */
  MenuItemProps?: MenuItemProps;
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @type {string | undefined}
   */
  className?: string;
  /**
   * (optional)
   * Label for the Icon Menu Item.
   * @property
   * @name label
   * @type {string | undefined}
   */
  label?: string;
  /**
   * (optional)
   * Icon on the left.
   * @default null
   * @property
   * @name leftIcon
   * @type {ReactNode}
   */
  leftIcon?: React.ReactNode;
  /**
   * (optional)
   * Callback fired when the item is clicked.
   * @property
   * @name onClick
   * @type {function}
   */
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /**
   * (optional)
   * Reference for the Styled Menu Item.
   * @property
   * @name ref
   * @type {React.RefObject<HTMLLIElement> | undefined}
   */
  ref?: RefObject<HTMLLIElement>;
  /**
   * (optional)
   * Icon on the right.
   * @default <ChevronRight />
   * @property
   * @name rightIcon
   * @type {ReactNode}
   */
  rightIcon?: React.ReactNode;
  /**
   * (optional)
   * Count of items filtered.
   * @property
   * @name filterCount
   * @type {number | undefined}
   */
  filterCount?: number;
};

/**
 * Memoized Icon Menu Item.
 * @constant
 * @name IconMenuItem
 * @kind variable
 * @type {React.MemoExoticComponent<React.ForwardRefExoticComponent<Omit<IconMenuItemProps, "ref"> & React.RefAttributes<HTMLLIElement>>>}
 */
const IconMenuItem: React.MemoExoticComponent<
  React.ForwardRefExoticComponent<
    Omit<IconMenuItemProps, "ref"> & React.RefAttributes<HTMLLIElement>
  >
> = memo(
  forwardRef<HTMLLIElement, IconMenuItemProps>(
    /**
     * Icon Menu Item.
     * @function
     * @name IconMenuItem
     * @kind function
     * @memberof IconMenuItem
     * @param {IconMenuItemProps} props Props for Icon Menu Item.
     * @param {React.ForwardedRef<HTMLLIElement>} ref Reference for Icon Menu Item.
     * @returns {JSX.Element} Icon Menu Item.
     */
    function IconMenuItem(
      {
        MenuItemProps,
        className,
        label,
        leftIcon,
        rightIcon,
        filterCount,
        ...props
      }: IconMenuItemProps,
      ref: React.ForwardedRef<HTMLLIElement>
    ): JSX.Element {
      return (
        <StyledMenuItem
          {...MenuItemProps}
          ref={ref}
          className={className}
          {...props}
        >
          <FlexBox>
            {leftIcon}
            <StyledTypography>{label}</StyledTypography>
            {filterCount && filterCount > 0 ? (
              <Chip
                className="inline-flex h-6 w-6 flex-col items-center justify-center gap-2.5 rounded-3xl bg-neutral-400 px-1.5 text-center text-xs font-medium text-white"
                label={filterCount}
                size="small"
              />
            ) : null}
          </FlexBox>
          {rightIcon}
        </StyledMenuItem>
      );
    }
  )
);

export default IconMenuItem;
